import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
    PlatformType,
    PaginationType,
    CategoryDataType,
    CategoryInfo,
    ShortGameZipType,
    ShortGameType,
    CategoryType,
    APIRedirect,
} from '@/types'
import { getGamesByCategories, getGamesCategoriesSeoById } from '@/utils/requests/games'
import { useAppStore } from '@/store/app-store'
import { shuffle, unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { useUrl } from '@/composables/url-helpers'
import { isAPIRedirect } from '@/utils/requests/utils'

// divisible by 2, 3, 4, 6 to make a nice grid with big cards
const PAGE_SIZE = 48
const SHUFFLE_SIZE = 16

type SetCategoryParams = {
    categoryName: string
    page: number
}

type UpdateListRequestParams = {
    categoryName: string
    platform: PlatformType
    pagination: PaginationType
    locale: string
}

type Category = {
    id: string
    categoryType: CategoryType
    total: number
    totalPages: number
}

function updateList({
    categoryName,
    platform,
    pagination,
    locale,
}: UpdateListRequestParams): Promise<CategoryDataType> {
    return getGamesByCategories({
        categoriesIds: [categoryName],
        platform,
        pagination,
        locale,
    })
}

export const useCategoryPageStore = defineStore('categoryPage', () => {
    const pagesRaw = ref<(ShortGameZipType[] | null)[]>([])
    const pages = computed<(ShortGameType[] | null)[]>(() =>
        pagesRaw.value.map((page) => (page ? page.map(unzipShortGame) : null)),
    )
    const pageSize = ref(PAGE_SIZE)
    const category = ref<Category | undefined>()
    const categoryInfo = ref<CategoryInfo | undefined>()
    const categoryRedirect = ref<APIRedirect | undefined>()

    const { getCategoryRoute } = useUrl()

    const appStore = useAppStore()
    const i18nAddon = useI18nAddon()

    async function setCategory(params: SetCategoryParams): Promise<void> {
        const { currentLocale } = i18nAddon

        const pageIndex = params.page - 1
        if (categoryInfo.value?.hru === params.categoryName && pagesRaw.value[pageIndex] !== undefined) {
            return
        }

        // this data is available only on second time this method is called
        // TODO load category meta separately
        if (typeof category.value?.totalPages === 'number') {
            if (params.page > category.value.totalPages) {
                return
            }
        }

        let categoryInfoPromise = Promise.resolve()
        if (categoryInfo.value?.hru !== params.categoryName) {
            pagesRaw.value = []
            category.value = undefined
            categoryInfo.value = undefined
            categoryRedirect.value = undefined
            categoryInfoPromise = getGamesCategoriesSeoById(params.categoryName, currentLocale.value).then(
                (categoryInfoData) => {
                    if (isAPIRedirect(categoryInfoData)) {
                        categoryRedirect.value = categoryInfoData
                        return
                    }

                    const type = categoryInfoData.categoryType as CategoryType
                    const pathCategories = categoryInfoData.pathCategories.map((path) => ({
                        ...path,
                        href: getCategoryRoute({ hru: path.hru, type }),
                    }))
                    const subCategories = categoryInfoData.subCategories.map((path) => ({
                        ...path,
                        href: getCategoryRoute({ hru: path.hru, type }),
                    }))
                    categoryInfo.value = {
                        ...categoryInfoData,
                        pathCategories,
                        subCategories,
                        fullTitle: categoryInfoData.description,
                        metaDescription: categoryInfoData.metaDescription,
                        metaTitle: categoryInfoData.metaTitle,
                    }
                },
            )
        }

        // TODO dangerous on server, when request is ?page=10000000000
        pagesRaw.value[pageIndex] = null

        const pagesPromise = updateList({
            categoryName: params.categoryName,
            pagination: {
                page: params.page,
                pageSize: pageSize.value,
            },
            platform: appStore.platform,
            locale: currentLocale.value,
        }).then(({ categories: [fetchedCategory] }) => {
            if (!fetchedCategory || fetchedCategory.games.length === 0) {
                // eslint-disable-next-line no-throw-literal
                throw 404
            }
            const zippedApiGames = fetchedCategory.games.map(zipAPIGame)
            const partlyShuffledGames = [
                ...shuffle(zippedApiGames.slice(0, SHUFFLE_SIZE)),
                ...zippedApiGames.slice(SHUFFLE_SIZE),
            ]
            pagesRaw.value[pageIndex] = pageIndex === 0 ? partlyShuffledGames : zippedApiGames
            category.value = {
                id: fetchedCategory.id,
                categoryType: fetchedCategory.categoryType || 'category',
                total: fetchedCategory.totalCount,
                totalPages: Math.ceil(fetchedCategory.totalCount / pageSize.value),
            }
            pagesRaw.value = pagesRaw.value.slice(0, category.value.totalPages)
        })

        await Promise.all([pagesPromise, categoryInfoPromise])
    }

    return {
        // expose to capture on SSR, serialize and hydrate on the client
        pagesRaw,
        pages,
        pageSize,
        category,
        categoryInfo,
        categoryRedirect,
        setCategory,
    }
})
